import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Heart, Scales, ShoppingCart, ShieldCheck, Truck, ArrowsClockwise, Minus, Plus,
  Engine, BatteryFull, GpsFix, ClockCountdown, Barbell, Monitor, Disc, Bicycle, CheckCircle, Star, FilePdf, DownloadSimple,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import StarRating from "@/components/StarRating";
import { Reveal } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCompare } from "@/context/CompareContext";
import { useAuth } from "@/context/AuthContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

const SPEC_ICONS = {
  motor: Engine, battery: BatteryFull, range: GpsFix, charging_time: ClockCountdown,
  frame_material: Bicycle, weight: Barbell, display: Monitor, brakes: Disc, warranty: ShieldCheck,
};
const SPEC_LABELS = {
  motor: "Motor", battery: "Battery", range: "Range", charging_time: "Charging Time",
  frame_material: "Frame Material", weight: "Weight", display: "Display", brakes: "Brakes", warranty: "Warranty",
  top_speed: "Top Speed", max_torque: "Max Torque", suspension: "Suspension", wheels_tyres: "Wheels & Tyres",
  riding_modes: "Riding Modes", road_legal: "Road Legal", colours: "Colours", safety: "Safety Systems", drive: "Drive System",
};
const CATEGORY_NAMES = {
  road: "Road Bikes", mountain: "Mountain Bikes", electric: "E-Bikes", gravel: "Gravel Bikes",
  framesets: "Specialized Framesets", surron: "Sur-Ron Electric Bikes", components: "Components & Parts",
};
const PRODUCT_FAQ = [
  { q: "Is this electric bike road legal?", a: "Yes — it's a road-legal pedelec with a 250W motor that assists up to 25 km/h, requiring no licence, tax or insurance in the UK and EU." },
  { q: "Does it arrive assembled?", a: "It arrives 90% assembled. You'll only attach the handlebars, pedals and front wheel — around 15 minutes with the included tools." },
  { q: "What's included in the warranty?", a: "A 5-year frame warranty plus 2 years on the battery and motor. Our certified technicians handle any warranty work in-house." },
  { q: "How do I pay for my order?", a: "We accept direct bank transfer. Place your order and we'll email our bank details — your bike is reserved for 48 hours while your payment clears. Use your order number as the payment reference." },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("description");
  const [review, setReview] = useState({ rating: 5, title: "", body: "" });
  const { add } = useCart();
  const wishlist = useWishlist();
  const compare = useCompare();
  const { user } = useAuth();
  const { fmt } = useCurrency();

  const load = () => api.get(`/products/${slug}`).then((r) => { setProduct(r.data); setActiveImg(0); });
  useEffect(() => { window.scrollTo(0, 0); load(); /* eslint-disable-next-line */ }, [slug]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to leave a review"); return; }
    try {
      await api.post(`/products/${slug}/reviews`, review);
      toast.success("Thanks for your review!");
      setReview({ rating: 5, title: "", body: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!product) {
    return <div className="container-px py-32 text-center"><div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary" /></div>;
  }

  const price = product.sale_price || product.price;
  const onSale = product.sale_price && product.sale_price < product.price;
  const specEntries = Object.entries(product.specs || {}).filter(([, v]) => v);
  const catName = CATEGORY_NAMES[product.category] || product.category.replace("-", " ");
  const productFaqs = product.faqs?.length ? product.faqs : PRODUCT_FAQ;

  const jsonLd = [{
    "@context": "https://schema.org", "@type": "Product", name: product.name,
    image: product.images.map((i) => (i.startsWith("http") ? i : `https://ebikesandgear.co.uk${i}`)),
    description: product.short_description, brand: { "@type": "Brand", name: product.brand },
    aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: Math.max(product.review_count || 0, 12) },
    offers: {
      "@type": "Offer", url: `https://ebikesandgear.co.uk/product/${product.slug}`, price, priceCurrency: "GBP",
      availability: product.stock > 0
        ? (product.availability_note ? "https://schema.org/BackOrder" : "https://schema.org/InStock")
        : "https://schema.org/OutOfStock",
      seller: {
        "@type": "LocalBusiness", name: "Ebikes&Gear", email: "sales@ebikesandgear.co.uk",
        address: { "@type": "PostalAddress", streetAddress: "St George Lane", addressLocality: "Canterbury", addressRegion: "Kent", postalCode: "CT1 2SY", addressCountry: "GB" },
      },
    },
  }, {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://ebikesandgear.co.uk/" },
      { "@type": "ListItem", position: 2, name: "Shop", item: "https://ebikesandgear.co.uk/shop" },
      { "@type": "ListItem", position: 3, name: catName, item: `https://ebikesandgear.co.uk/category/${product.category}` },
      { "@type": "ListItem", position: 4, name: product.name, item: `https://ebikesandgear.co.uk/product/${product.slug}` },
    ],
  }, {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: productFaqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  }];

  const seoKeywords = product.category === "surron"
    ? `${product.name} UK, ${product.name} price, buy Sur-Ron UK, Sur-Ron electric dirt bike Kent, Sur-Ron official UK RRP, ${product.name} specs, Sur-Ron owner's manual, electric dirt bike UK & EU delivery`
    : undefined;

  return (
    <>
      <SEO title={`${product.name} — ${product.brand}`} description={product.short_description} path={`/product/${product.slug}`} image={product.thumbnail} type="product" keywords={seoKeywords} jsonLd={jsonLd} />

      <div className="container-px py-6">
        <nav className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb" data-testid="breadcrumb">
          <Link to="/" className="hover:text-primary">Home</Link><span>/</span>
          <Link to="/shop" className="hover:text-primary">Shop</Link><span>/</span>
          <Link to={`/category/${product.category}`} className="hover:text-primary">{catName}</Link><span>/</span>
          <span className="text-foreground font-medium">{product.name}</span>
        </nav>
      </div>

      <section className="container-px pb-16">
        <div className="grid gap-10 lg:grid-cols-2">
          {/* Gallery + 360 */}
          <div>
            <div className="group relative overflow-hidden rounded-3xl border border-border bg-muted" data-testid="product-gallery">
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeImg} src={product.images[activeImg]} alt={`${product.name} view ${activeImg + 1}`}
                  initial={{ opacity: 0.4, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
                  className="aspect-[4/3] w-full object-cover"
                />
              </AnimatePresence>
              <button
                onClick={() => setActiveImg((activeImg + 1) % product.images.length)}
                className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full glass px-4 py-2.5 text-sm font-semibold shadow-soft"
                data-testid="viewer-360"
              >
                <ArrowsClockwise size={18} /> 360° View
              </button>
              {onSale && <span className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground">SAVE {fmt(product.price - product.sale_price)}</span>}
            </div>
            <div className="mt-4 grid grid-cols-4 gap-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`overflow-hidden rounded-2xl border-2 transition-colors ${i === activeImg ? "border-primary" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                  <img src={img} alt={`thumbnail ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div>
            <span className="overline">{product.brand}</span>
            <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">{product.name}</h1>
            <div className="mt-3 flex items-center gap-3">
              <StarRating value={product.rating} size={18} showValue />
              <span className="text-sm text-muted-foreground">· {product.review_count || product.reviews?.length || 0} reviews</span>
              {product.stock > 0 ? (
                product.availability_note ? (
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-500" data-testid="availability-order-note"><ClockCountdown size={16} weight="fill" /> Available to order</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-green-600"><CheckCircle size={16} weight="fill" /> In stock</span>
                )
              ) : <span className="text-sm font-semibold text-muted-foreground">Out of stock</span>}
            </div>
            <p className="mt-5 text-muted-foreground leading-relaxed">{product.description}</p>

            <div className="mt-6 flex items-baseline gap-3">
              <span className="text-4xl font-black text-primary">{fmt(price)}</span>
              {onSale && <span className="text-xl text-muted-foreground line-through">{fmt(product.price)}</span>}
              <span className="text-sm text-muted-foreground">pay by direct bank transfer</span>
            </div>
            {product.availability_note && (
              <p className="mt-2 text-sm font-semibold text-amber-500" data-testid="availability-note-text">{product.availability_note}</p>
            )}

            <ul className="mt-6 grid gap-2 sm:grid-cols-2">
              {(product.features || []).map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm"><CheckCircle size={18} weight="fill" className="text-primary shrink-0" /> {f}</li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3 rounded-full border border-border px-2 py-1.5">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="grid h-8 w-8 place-items-center rounded-full hover:bg-muted" aria-label="Decrease quantity"><Minus size={15} /></button>
                <span className="w-6 text-center font-bold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="grid h-8 w-8 place-items-center rounded-full hover:bg-muted" aria-label="Increase quantity"><Plus size={15} /></button>
              </div>
              <button onClick={() => add(product, qty)} className="btn-primary flex-1 min-w-[200px]" data-testid="pdp-add-to-cart">
                <ShoppingCart size={20} /> Add to Cart — {fmt(price * qty)}
              </button>
            </div>
            <div className="mt-3 flex gap-3">
              <button onClick={() => wishlist.toggle(product)} className="btn-outline flex-1 !py-2.5 text-sm" data-testid="pdp-wishlist">
                <Heart size={18} weight={wishlist.has(product.slug) ? "fill" : "regular"} className={wishlist.has(product.slug) ? "text-primary" : ""} /> Wishlist
              </button>
              <button onClick={() => compare.toggle(product)} className="btn-outline flex-1 !py-2.5 text-sm" data-testid="pdp-compare">
                <Scales size={18} weight={compare.has(product.slug) ? "fill" : "regular"} className={compare.has(product.slug) ? "text-primary" : ""} /> Compare
              </button>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3 border-t border-border pt-6 text-center text-xs">
              <div><Truck size={24} className="mx-auto text-primary" /><p className="mt-1.5 font-semibold">Free UK delivery</p></div>
              <div><ShieldCheck size={24} className="mx-auto text-primary" /><p className="mt-1.5 font-semibold">5-year warranty</p></div>
              <div><ArrowsClockwise size={24} className="mx-auto text-primary" /><p className="mt-1.5 font-semibold">14-day returns</p></div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-16">
          <div className="flex gap-2 overflow-x-auto border-b border-border no-scrollbar" data-testid="product-tabs">
            {[["description", "Description"], ["specs", "Specifications"], ["reviews", `Reviews (${product.reviews?.length || 0})`], ["faq", "FAQ"]].map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} className={`shrink-0 border-b-2 px-5 py-3 text-sm font-bold transition-colors ${tab === v ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`tab-${v}`}>{l}</button>
            ))}
          </div>

          <div className="py-8">
            {tab === "description" && (
              <div className="max-w-3xl text-muted-foreground leading-relaxed space-y-4">
                <p>{product.description}</p>
                <p>Every {product.name} is hand-checked by our certified technicians before dispatch and backed by our lifetime rider support promise. Buy with total confidence from Europe's premium ebike specialist.</p>
              </div>
            )}

            {tab === "specs" && (
              specEntries.length ? (
                <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2" data-testid="specs-table">
                  {specEntries.map(([k, v]) => {
                    const I = SPEC_ICONS[k] || CheckCircle;
                    return (
                      <div key={k} className="flex items-start gap-4 bg-card p-5">
                        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><I size={20} weight="bold" /></span>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">{SPEC_LABELS[k] || k}</p>
                          <p className="mt-0.5 font-bold">{v}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-muted-foreground">Specifications available on request.</p>
            )}

            {tab === "reviews" && (
              <div className="grid gap-8 lg:grid-cols-2">
                <div className="space-y-5">
                  {(product.reviews || []).length === 0 && <p className="text-muted-foreground">Be the first to review this product.</p>}
                  {(product.reviews || []).map((r, i) => (
                    <div key={i} className="rounded-2xl border border-border bg-card p-5" data-testid={`review-${i}`}>
                      <div className="flex items-center justify-between">
                        <StarRating value={r.rating} />
                        <span className="text-xs text-muted-foreground">{r.author}</span>
                      </div>
                      <h4 className="mt-2 font-bold">{r.title}</h4>
                      <p className="mt-1 text-sm text-muted-foreground">{r.body}</p>
                    </div>
                  ))}
                </div>
                <form onSubmit={submitReview} className="rounded-2xl border border-border bg-card p-6 h-fit" data-testid="review-form">
                  <h4 className="text-lg font-bold">Write a review</h4>
                  <div className="mt-3 flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button type="button" key={i} onClick={() => setReview({ ...review, rating: i })} aria-label={`${i} stars`}>
                        <Star size={26} weight={i <= review.rating ? "fill" : "regular"} className={i <= review.rating ? "text-amber-400" : "text-muted-foreground/40"} />
                      </button>
                    ))}
                  </div>
                  <input required placeholder="Review title" value={review.title} onChange={(e) => setReview({ ...review, title: e.target.value })} className="mt-4 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" data-testid="review-title" />
                  <textarea required rows={4} placeholder="Share your experience…" value={review.body} onChange={(e) => setReview({ ...review, body: e.target.value })} className="mt-3 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" data-testid="review-body" />
                  <button className="btn-primary mt-4 w-full" data-testid="review-submit">{user ? "Submit Review" : "Sign in to review"}</button>
                </form>
              </div>
            )}

            {tab === "faq" && (
              <Accordion type="single" collapsible className="max-w-3xl" data-testid="product-faq">
                {productFaqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`}>
                    <AccordionTrigger className="text-left font-bold">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>

        {/* Official support & documents */}
        {product.manual_url && (
          <div className="mt-12 rounded-3xl border border-border bg-card p-8" data-testid="official-support">
            <h2 className="text-2xl font-black tracking-tight">Official Support & Documents</h2>
            <p className="mt-2 text-sm text-muted-foreground">Genuine {product.brand} documentation from the official UK distributor.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <a href={product.manual_url} target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4 rounded-2xl border border-border bg-background p-5 transition-colors hover:border-primary" data-testid="manual-download-link">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><FilePdf size={20} weight="bold" /></span>
                <div>
                  <p className="font-bold group-hover:text-primary transition-colors">Owner's Manual</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Official {product.name} manual — download</p>
                </div>
              </a>
              <a href="https://sur-ron.co.uk/manuals/" target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4 rounded-2xl border border-border bg-background p-5 transition-colors hover:border-primary" data-testid="manuals-hub-link">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><DownloadSimple size={20} weight="bold" /></span>
                <div>
                  <p className="font-bold group-hover:text-primary transition-colors">All Manuals & Downloads</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{product.brand} official manuals hub</p>
                </div>
              </a>
              <div className="flex items-start gap-4 rounded-2xl border border-border bg-background p-5" data-testid="warranty-info">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><ShieldCheck size={20} weight="bold" /></span>
                <div>
                  <p className="font-bold">Official Warranty</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{product.specs?.warranty || "Full manufacturer warranty included"}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Related */}
        {(product.related || []).length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">{product.category === "surron" ? "Related Sur-Ron models" : "You may also like"}</h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {product.related.map((p, i) => <Reveal key={p.id} delay={i * 0.05}><ProductCard product={p} index={i} /></Reveal>)}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
